import { FiBarChart2 } from 'react-icons/fi';
import type { NavItem } from '@/app/config/navigation';

/**
 * Tenants module manifest.
 *
 * A module's manifest is the registration that the navigation registry and
 * guards consume. The build-time allowlist is the only place a module's
 * surface is declared: no runtime discovery, no tenant-generated additions.
 *
 * THE PERMISSION CODES ARE THE API'S
 * ----------------------------------
 * `tenant.read` and `tenant.create` are the PLATFORM grants the API issues
 * (`platform_permission_grants`, seeded by migration 0124 for `tenant.read`).
 * They are PLATFORM authority: the merchant registry of this deployment. A
 * tenant administrator holds neither, so the merchant registry is never
 * offered to one.
 *
 * The earlier `platform.tenant.read` / `platform.tenant.write` codes were
 * INVENTED — the API never issued them, so they gated nothing and read as if
 * they did. The codes below are the ones the server actually holds.
 *
 * TENANT WORKSPACE PERMISSIONS
 * ----------------------------
 * The tenant workspace uses vendor.* permissions which are TENANT-SCOPED.
 * These require a valid tenant membership and are checked by the API per-request.
 * The workspace route itself is not in the platform manifest — it's accessed
 * via the tenant detail page's "Workspace" tab.
 */
export const TENANTS_READ_PERMISSION = 'tenant.read';
export const TENANTS_CREATE_PERMISSION = 'tenant.create';

/**
 * Platform grants for the tenant-management extensions (register v1.4).
 *
 * `tenant.update` gates the invitation, onboarding-review and platform-assisted
 * onboarding surfaces. `tenant.verification.review` gates the M08 verification
 * review surface. Both are PLATFORM authority, held by a platform operator and
 * never by a tenant administrator.
 */
export const TENANTS_UPDATE_PERMISSION = 'tenant.update';
export const TENANT_VERIFICATION_REVIEW_PERMISSION = 'tenant.verification.review';

// Tenant-scoped vendor permissions (used by workspace APIs)
export const VENDOR_PROFILE_READ_PERMISSION = 'vendor.profile.read';
export const VENDOR_PROFILE_UPDATE_PERMISSION = 'vendor.profile.update';
export const VENDOR_LOCATION_READ_PERMISSION = 'vendor.location.read';
export const VENDOR_LOCATION_CREATE_PERMISSION = 'vendor.location.create';
export const VENDOR_LOCATION_UPDATE_PERMISSION = 'vendor.location.update';
export const VENDOR_LOCATION_PUBLISH_PERMISSION = 'vendor.location.publish';

export const MANIFEST = {
  id: 'tenants',
  routes: [
    { path: '/platform/tenants', permission: TENANTS_READ_PERMISSION, scope: 'platform' },
    { path: '/platform/tenants/new', permission: TENANTS_CREATE_PERMISSION, scope: 'platform' },
    { path: '/platform/tenants/:id', permission: TENANTS_READ_PERMISSION, scope: 'platform' },
    // Tenant workspace is accessed via tenant detail page, not a direct platform route
  ],
} as const;

export const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    id: 'platform.tenants',
    to: '/platform/tenants',
    label: 'Tenants',
    group: 'Platform',
    order: 20,
    permissionScope: 'platform',
    permission: { require: [TENANTS_READ_PERMISSION] },
    icon: FiBarChart2.name,
  },
];
